import { useCallback, useState, useMemo } from "react"
import { removeAt } from "../Helpers/Functions/array"

function toggleItemInArray<M>(
    item: M,
    getId: (model: M) => string,
    currentItems: M[],
    allItems: M[]
): M[] {
    const idToToggle = getId(item)

    for (let j = 0, i = 0; i < currentItems.length; i++) {
        const id = getId(currentItems[i])

        if (id === idToToggle) {
            return removeAt(currentItems, i)
        }

        for (; getId(allItems[j]) !== id; j++) {
            if (getId(allItems[j]) === idToToggle) {
                return currentItems
                    .slice(0, i)
                    .concat([item], currentItems.slice(i))
            }
        }
    }

    return currentItems.concat([item])
}

export function useSelection<M>(
    getId: (model: M) => string,
    collection: M[]
): [M[], (model: M) => void, () => void] {
    const [selection, setSelection] = useState<M[]>([])
    const handleToggle = useCallback(
        (model: M) => {
            setSelection(prevSelection =>
                toggleItemInArray(model, getId, prevSelection, collection)
            )
        },
        [setSelection, getId, collection]
    )
    const handleClear = useCallback(() => setSelection([]), [setSelection])

    // look up from provided `collection` to ensure the models are not stale.
    const validSelection = useMemo(
        () =>
            selection.flatMap(sel => {
                const id = getId(sel)
                const model = collection.find(m => getId(m) === id)

                return model === undefined ? [] : [model]
            }),
        [selection, getId, collection]
    )

    return [validSelection, handleToggle, handleClear]
}
